import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/OfflineIndicator.css';

/**
 * OfflineIndicator - Network connectivity status banner
 *
 * Features:
 * - Detects online/offline status
 * - Slide-in animation when offline
 * - Auto-hide when back online
 * - Mobile-safe-area aware
 * - Retry button for manual check
 */
function OfflineIndicator() {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);

      // Show "reconnected" message briefly if we were offline
      if (wasOffline) {
        setShowReconnected(true);
        setTimeout(() => {
          setShowReconnected(false);
          setWasOffline(false);
        }, 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setShowReconnected(false);
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic connectivity check (every 30 seconds when offline)
    let checkInterval;
    if (!isOnline) {
      checkInterval = setInterval(() => {
        // Attempt to fetch a small resource to verify connectivity
        fetch('/favicon.ico', {
          method: 'HEAD',
          cache: 'no-cache'
        })
          .then(() => {
            if (!navigator.onLine) {
              // Trigger online event manually if navigator.onLine is stale
              window.dispatchEvent(new Event('online'));
            }
          })
          .catch(() => {
            // Still offline
          });
      }, 30000);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [isOnline, wasOffline]);

  // Manual retry
  const handleRetry = () => {
    // Force a connectivity check
    fetch('/favicon.ico', {
      method: 'HEAD',
      cache: 'no-cache'
    })
      .then(() => {
        window.dispatchEvent(new Event('online'));
      })
      .catch(() => {
        // Still offline, show feedback
        console.log('Still offline');
      });
  };

  // Don't render anything if we're online and haven't been offline
  if (isOnline && !showReconnected) {
    return null;
  }

  return (
    <>
      {/* Offline Banner */}
      {!isOnline && (
        <div className="offline-indicator offline" role="alert" aria-live="assertive">
          <div className="offline-content">
            <span className="offline-icon">📡</span>
            <div className="offline-text">
              <strong className="offline-title">
                {t('offline.title')}
              </strong>
              <span className="offline-message">
                {t('offline.message')}
              </span>
            </div>
            <button
              className="offline-retry-btn"
              onClick={handleRetry}
              aria-label={t('offline.retry')}
            >
              <span className="retry-icon">🔄</span>
            </button>
          </div>
        </div>
      )}

      {/* Reconnected Banner */}
      {showReconnected && (
        <div className="offline-indicator online" role="status" aria-live="polite">
          <div className="offline-content">
            <span className="offline-icon">✓</span>
            <div className="offline-text">
              <strong className="offline-title">
                {t('offline.reconnected')}
              </strong>
              <span className="offline-message">
                {t('offline.reconnectedMessage')}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default OfflineIndicator;
